import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Database,
  Home,
  Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router-dom'

import { cn } from '../lib/utils'

interface NavItem {
  to: string
  key: 'home' | 'explorer' | 'studio' | 'sources'
  icon: typeof Home
  end?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', key: 'home', icon: Home, end: true },
  { to: '/explorer', key: 'explorer', icon: BarChart3 },
  { to: '/studio', key: 'studio', icon: Sparkles },
  { to: '/sources', key: 'sources', icon: Database },
]

/** Logo estilizado para Lapland Marketing Assistant */
export function BrandLogo({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-1 py-1 min-h-[44px]">
      <div className="relative flex size-9 shrink-0 items-center justify-center self-center rounded-xl bg-gradient-to-br from-cyan-500 via-sky-500 to-indigo-600 shadow-md shadow-cyan-500/25 ring-1 ring-white/20 text-white">
        <Sparkles className="size-4.5 text-cyan-100" />
        <span className="absolute -top-1 -right-1 flex size-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex size-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
        </span>
      </div>
      {!collapsed ? (
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-sm font-bold tracking-tight leading-none text-foreground">
            Lapland Marketing Assistant
          </span>
          <span className="text-[10px] font-medium tracking-wider uppercase text-muted-foreground/90 mt-1">
            Visit Levi · DMO
          </span>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Contenido del menú lateral
 */
export function SidebarContent({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex h-full flex-col gap-4 p-3 sm:p-4 overflow-y-auto overflow-x-hidden">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2 shrink-0">
        <BrandLogo collapsed={collapsed} />
      </Link>

      <nav className="flex flex-col gap-1.5 shrink-0" aria-label={t('app.title')}>
        {NAV_ITEMS.map(({ to, key, icon: Icon, end }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            onClick={onNavigate}
            title={collapsed ? t(`nav.${key}`) : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                collapsed ? 'justify-center px-2' : '',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <Icon className="size-4 shrink-0" />
            {!collapsed ? <span>{t(`nav.${key}`)}</span> : null}
          </NavLink>
        ))}
      </nav>

      <div className="flex-1 min-h-4" />

      {/* Powered by NADIA Badge - Discreto y ligero en la base */}
      <div className={cn('pt-1 pb-1 text-center shrink-0 border-t border-border/40', collapsed ? 'px-1' : 'px-2')}>
        {!collapsed ? (
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span>Powered by</span>
              <span className="font-semibold text-foreground/90">NADIA Engine</span>
            </div>
            <span className="text-[9px] text-muted-foreground/70 tracking-wide">
              ETDS Tourism Data Space
            </span>
          </div>
        ) : (
          <div title="Powered by NADIA Engine" className="flex justify-center py-1">
            <span className="font-mono text-[10px] font-bold text-muted-foreground hover:text-primary transition-colors">
              NADIA
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/** Barra lateral fija y colapsable de escritorio */
export function Sidebar({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean
  onToggleCollapse: () => void
}) {
  return (
    <aside
      className={cn(
        'bg-card sticky top-0 z-30 hidden h-screen shrink-0 border-r md:block transition-all duration-300',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent collapsed={collapsed} />

      {/* Botón flotante para esconder/mostrar la barra */}
      <button
        type="button"
        onClick={onToggleCollapse}
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        className="absolute -right-3 top-6 z-40 flex size-6 items-center justify-center rounded-full border bg-background shadow-md hover:bg-accent transition-transform"
      >
        {collapsed ? <ChevronRight className="size-3.5" /> : <ChevronLeft className="size-3.5" />}
      </button>
    </aside>
  )
}